import { HeaderSidebar } from "../../../components/Header/HeaderSidebar"
import { Formik, Form, FieldArray, Field } from "formik"
import { useEffect, useState } from "react"
import { getClassName } from "../../../utils";
import { Slider, styled } from "@mui/material"
import { AddMore } from "../../../components/Buttons/AddMoreButton";
import { StepperButtons } from "../../../components/Stepper/Buttons";
import { getAttributesForm } from "../../../services/autofillService";
import { useNavigate } from "react-router-dom";
import { Step } from "../../../components/Step/Step";
import { questions, videos } from "../../../content/personality/attributes";
import { useDispatch } from "react-redux";
import { setProgress } from "../../../store/progressSlice";
import { ProgressLine } from "../../../components/Progress/Line";
import { submitAttributes } from "../../../services/answersService";

export function fieldToSlider({ field, form: { isSubmitting }, ...props }) {
    return {
        ...props,
        ...field,
        name: field.name,
        value: field.value
    }
}

const iOSBoxShadow =
    '0 3px 1px rgba(0,0,0,0.1),0 4px 8px rgba(0,0,0,0.13),0 0 0 1px rgba(0,0,0,0.02)';

export const CustomSlider = props => {
    return (
        <Slider {...fieldToSlider(props)} onChange={(e, value) => {
            props.form.setFieldValue(props.field.name, value);
        }} />
    )
}

const discreteSlider = styled(CustomSlider)(({ theme }) => ({
    color: theme.palette.mode === 'dark' ? '#fce816' : '#fce816',
    height: 2,
    padding: '15px 0',
    '&.MuiSlider-root': {
        flex: 2,
    },
    '& .MuiSlider-thumb': {
        height: 20,
        width: 20,
        backgroundColor: '#fce816',
        boxShadow: iOSBoxShadow,
        '&:focus, &:hover, &.Mui-active': {
            boxShadow:
                '0 3px 1px rgba(0,0,0,0.1),0 4px 8px rgba(0,0,0,0.3),0 0 0 1px rgba(0,0,0,0.02)',
            // Reset on touch devices, it doesn't add specificity
            '@media (hover: none)': {
                boxShadow: iOSBoxShadow,
            },
        },
    },
    '& .MuiSlider-valueLabel': {
        fontSize: 12,
        fontWeight: 'normal',
        top: -6,
        backgroundColor: 'unset',
        color: theme.palette.text.primary,
        '&:before': {
            display: 'none',
        },
        '& *': {
            background: 'transparent',
            color: theme.palette.mode === 'dark' ? '#fff' : '#000',
        },
    },
    '& .MuiSlider-track': {
        border: 'none',
        backgroundColor: '#bfbfbf',
        opacity: 0.5,
    },
    '& .MuiSlider-rail': {
        opacity: 0.5,
        backgroundColor: '#bfbfbf',
    },
    '& .MuiSlider-mark': {
        backgroundColor: '#bfbfbf',
        height: 20,
        width: 1,
        '&.MuiSlider-markActive': {
            opacity: 1,
            backgroundColor: '#bfbfbf',
        },
    },
}))

export const PersonalityAttributes = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(0);
    const [activeStep, setActiveStep] = useState(0);
    const [initialValues, setInitialValues] = useState({
        personalityAttributes: [
            {
                leftAttribute: '',
                personalityAttribute: 4,
                rightAttribute: ''
            }
        ]
    });
    const [showNext, setShowNext] = useState(false);

    const marks = [
        {
            value: 1,
        },
        {
            value: 2,
        },
        {
            value: 3,
        },
        {
            value: 4,
        },
        {
            value: 5,
        },
        {
            value: 6,
        },
        {
            value: 7,
        },
    ];

    const handleSubmit = values => {
        setIsLoading(true);
        submitAttributes(values).then(response => {
            setIsLoading(false);
            navigate("/flow/relationships/competition");
        }).catch(err => {
            setIsLoading(false);
            console.error(err);
        })
    }

    useEffect(() => {
        dispatch(setProgress(90))
        getAttributesForm().then(response => {
            const data = response.data;
            if (data) {
                setInitialValues(data);
            }
        }).catch(err => {
            console.error(err);
        })
    }, []);
    return (
        <>
            <ProgressLine />
            <section className="h-dvh overflow-hidden flex flex-col items-center justify-center">
                <Formik
                    initialValues={initialValues}
                    enableReinitialize
                    onSubmit={handleSubmit}
                >
                    {({ values, errors, touched }) => (
                        <Form className="w-full max-w-[45%] h-full relative flex flex-col items-start justify-center">
                            <section className="flex flex-col items-center justify-center w-full h-full">
                                <Step className={getClassName(0, activeStep)} question={questions.personalityAttributes} url={videos.personalityAttributesVideo.url}>
                                    <FieldArray name="personalityAttributes">
                                        {({ insert, remove, push }) => (
                                            <>
                                                {values.personalityAttributes.length > 0 &&
                                                    values.personalityAttributes.map((personalityAttribute, index) => (
                                                        <fieldset className="flex gap-5" key={index}>
                                                            <Field
                                                                name={`personalityAttributes.${index}.leftAttribute`}
                                                                id="leftAttribute"
                                                                className="attributes has-border-bottom mt-3 flex-1"
                                                                placeholder="Attribute 1"
                                                            />
                                                            <Field
                                                                component={discreteSlider}
                                                                name={`personalityAttributes.${index}.personalityAttribute`}
                                                                id="personalityAttribute"
                                                                defaultValue={2}
                                                                aria-labelledby="discrete-slider-restrict"
                                                                step={null}
                                                                marks={marks}
                                                                min={1}
                                                                max={7}
                                                            />
                                                            <Field
                                                                name={`personalityAttributes.${index}.rightAttribute`}
                                                                id="rightAttribute"
                                                                className="attributes has-border-bottom mt-3 flex-1"
                                                                placeholder="Attribute 2"
                                                            />
                                                        </fieldset>
                                                    ))
                                                }
                                                <AddMore onClick={() => push({ leftAttribute: '', personalityAttribute: null, rightAttribute: '' })} />
                                            </>
                                        )}
                                    </FieldArray>
                                </Step>
                            </section>
                            <section className="flex w-full items-center mb-[10%] justify-between z-10">
                                <StepperButtons
                                    link="/flow/personality/tone"
                                    isLoading={isLoading}
                                    showNext={showNext || activeStep === 0}
                                />
                            </section>
                        </Form>
                    )}
                </Formik>
            </section>
            <HeaderSidebar activeEl="Personality & Charisma" />
        </>
    )
}